/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useCallback } from 'react';
import { Braces2 } from '@saleshandy/icons';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { t } from 'i18next';
import Editor from '../../../../../../../shared/editor/editor';
import TagsDropdown from '../../../../../../../shared/components/tags-dropdown';
import EmojiPicker from '../../../../../../../shared/design-system/components/atoms/emoji-picker';
import {
  dateChipTags,
  insertChipForDateTag,
  insertChipForMergeTag,
  insertChipForSpinTag,
} from '../../../../../../../shared/editor/helpers';
import {
  isValidHTML,
  nl2br,
} from '../sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../shared/design-system/components/overlay';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import SpintaxDropdown from '../../../../../../../shared/components/spintax-dropdown/spintax-dropdown';
import { TextEditorSkeleton } from '../ai-variant-skeletons';
import { AiVariantSkeletons } from '../ai-variant-skeletons/constants';
import { TinyMceEditorInputProps } from '../../types';
import { Images } from '../../../../../../../shared/app-constants';

const SingleEditorInput: React.FC<TinyMceEditorInputProps> = ({
  inputValue,
  inputErrorMessage,
  placeholder,
  isGeneratingWithAi = false,
  disableEditMode,
  inputRef,
  onSubjectKeyUp,
  onInputChange,
  onInputBlur,
  mergeTagOptions,
  sendSpintaxMixpanelEvent,
  showTitle = 'Subject',
  enableMergeTag = true,
  enableSpintax = true,
  enableEmoji = true,
  setError,
  inputRecommendation = '',
  showInputCount,
  showInput = true,
  name = 'subject',
  inputClassName,
  showInlineCount = false,
  maxInputCount,
  getEditorInstance,
  error = false,
  onTemplateApplied = false,
  onTemplateAppliedReset,
  sendThisEmailInSameThreadCheckboxValue = false,
  showDivider = true,
  isTitleDisabled = false,
}) => {
  const [emojiPicker, setEmojiPicker] = useState(false);
  const [showSpintax, setShowSpintax] = useState(false);
  const editorRef = useRef(null);
  const [editorInstance, setEditorInstance] = useState(null);

  const handleOnSpintaxToggle = useCallback(
    (value: boolean) => {
      if (!disableEditMode && enableSpintax) {
        setShowSpintax(value);
      }
    },
    [disableEditMode, enableSpintax],
  );

  const handleShowEmojiPicker = useCallback(() => {
    if (!disableEditMode && enableEmoji) {
      setEmojiPicker(true);
    }
  }, [disableEditMode, enableEmoji]);

  const handleHideEmojiPicker = useCallback(() => {
    if (enableEmoji) {
      setEmojiPicker(false);
    }
  }, [enableEmoji]);

  const handleAddEmoji = useCallback(
    (value) => {
      if (editorInstance && enableEmoji) {
        editorInstance.insertContent(value.native);
        handleHideEmojiPicker();
      }
    },
    [editorInstance, enableEmoji, handleHideEmojiPicker],
  );

  const handleAddMergeTag = useCallback(
    (option) => {
      if (editorInstance) {
        const { tag } = option;
        const uuid = uuidv4();

        let formattedTag = tag;
        const lastPipeIndex = tag.lastIndexOf('|');

        if (lastPipeIndex !== -1) {
          const beforePipe = tag.slice(0, lastPipeIndex + 1);
          const afterPipe = tag.slice(lastPipeIndex + 1);

          formattedTag =
            beforePipe +
            (isValidHTML(afterPipe) ? afterPipe : nl2br(afterPipe));
        }

        editorInstance.insertContent(
          insertChipForMergeTag(
            formattedTag,
            uuid,
            false,
            true,
            option?.fieldType,
          ),
        );

        const content = editorInstance.getContent();

        const syntheticEvent = {
          target: {
            name,
            value: content,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onInputChange(content, syntheticEvent);
      }
    },
    [editorInstance, name, onInputChange],
  );

  const handleMergeTagSelection = useCallback(
    (option) => {
      if (enableMergeTag) {
        handleAddMergeTag(option);
      }
    },
    [enableMergeTag, handleAddMergeTag],
  );

  const handleAddVariableTag = useCallback(
    (value) => {
      if (editorInstance && enableMergeTag) {
        const uuid = uuidv4();
        if (dateChipTags(value)) {
          editorInstance?.insertContent(
            insertChipForDateTag(value, uuid, true),
          );
        } else {
          editorInstance?.insertContent(
            insertChipForMergeTag(value, uuid, true),
          );
        }
        // editorInstance.insertContent(value);

        const content = editorInstance.getContent();

        const syntheticEvent = {
          target: {
            name,
            value: content,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onInputChange(content, syntheticEvent);
      }
    },
    [editorInstance, enableMergeTag, name, onInputChange],
  );

  const handleAddSpintax = useCallback(
    (value: string) => {
      if (editorInstance && enableSpintax) {
        const uuid = uuidv4();
        editorInstance.insertContent(insertChipForSpinTag(value, uuid));
        sendSpintaxMixpanelEvent?.();
      }
    },
    [editorInstance, enableSpintax, sendSpintaxMixpanelEvent],
  );

  const handleEditorChange = useCallback(
    (content: string) => {
      const syntheticEvent = {
        target: {
          name,
          value: content,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onInputChange(content, syntheticEvent);
    },
    [onInputChange, name],
  );

  const handleGetEditorInstance = useCallback(
    (editor) => {
      setEditorInstance(editor);
      if (getEditorInstance) {
        getEditorInstance(editor);
      }
    },
    [getEditorInstance],
  );
  const handleEditorBlur = useCallback(
    (content: string) => {
      if (!onInputBlur) {
        return;
      }

      const syntheticEvent = ({
        target: {
          name,
          value: content,
        },
      } as unknown) as React.FocusEvent<HTMLInputElement>;

      onInputBlur(syntheticEvent);
    },
    [name, onInputBlur],
  );

  // eslint-disable-next-line consistent-return
  const handleEditorKeyDown = useCallback((e) => {
    // Prevent Enter key from creating new lines
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, []);

  const subjectClassName = classNames([
    'email-modal-input-wrapper',
    {
      'input-with-error': inputErrorMessage,
    },
    inputClassName,
  ]);

  const helperWrapperClassName = classNames(
    {
      'helper-wrapper': showInputCount,
    },
    {
      'with-recommendation': inputRecommendation,
    },
  );

  return (
    <>
      <div
        className={`single-line-editor-input ${
          showTitle ? '' : 'single-line-editor-container'
        }`}
        ref={inputRef}
      >
        {showInput && (
          <div className={subjectClassName}>
            {isGeneratingWithAi && (
              <div className="ai-generated-subject-loading">
                <TextEditorSkeleton
                  textEditorSkeletonsList={AiVariantSkeletons.subjectSkeleton}
                />
              </div>
            )}

            {showTitle && (
              <>
                <p
                  className={`email-modal-input-label regular-1 ${
                    isTitleDisabled ? 'disabled-label' : ''
                  }`}
                  style={{
                    opacity: isTitleDisabled ? 0.6 : 1,
                    color: isTitleDisabled ? '#999' : undefined,
                  }}
                >
                  {showTitle}
                </p>
                {showDivider && <div className="email-modal-input-divider" />}
              </>
            )}

            <div
              className="subject-editor-wrapper"
              style={{
                cursor: disableEditMode ? 'not-allowed' : 'text',
              }}
            >
              <Editor
                ref={editorRef}
                value={inputValue}
                onEditorChange={handleEditorChange}
                getEditorInstance={handleGetEditorInstance}
                onKeyDown={handleEditorKeyDown}
                showMinimalToolbar={false}
                disabled={disableEditMode}
                placeholder={placeholder}
                onEditorBlur={() => handleEditorBlur(inputValue)}
                error={error}
                className={`email-modal-input-box ${
                  // eslint-disable-next-line no-nested-ternary
                  disableEditMode
                    ? 'disbale-subject-text'
                    : inputErrorMessage
                    ? 'single-line-editor-error'
                    : ''
                }`}
                isInline={true}
                isToolbardynamic=""
                mergeTags={mergeTagOptions}
                addMergeTagInContent={handleAddMergeTag}
                addVariableTagInContent={handleAddVariableTag}
                showMergeAndVariableTags={enableMergeTag}
                showAddAttachments={false}
                onPaste={
                  (enableMergeTag || enableSpintax) &&
                  ((content) =>
                    content
                      ?.replace(/<\/?div[^>]*>/gi, '')
                      ?.replace(/[\r\n]+/g, ' ')
                      ?.replace(/\s+/g, ' ')
                      ?.trim())
                }
                hideHeader={true}
                showCount={showInlineCount}
                maxInputCount={showInlineCount && maxInputCount}
                onTemplateApplied={onTemplateApplied}
                onTemplateAppliedReset={onTemplateAppliedReset}
                sendThisEmailInSameThreadCheckbox={
                  sendThisEmailInSameThreadCheckboxValue
                }
                singleLineContent
              />

              {/* Custom toolbar with icons */}
              <div
                className={`subject-editor-icons ${
                  disableEditMode ? 'disabled' : ''
                }`}
                style={{
                  cursor: disableEditMode ? 'not-allowed' : 'default',
                }}
              >
                {enableEmoji && (
                  <EmojiPicker
                    showEmojiPicker={emojiPicker}
                    onEmojiChange={handleAddEmoji}
                    onClick={handleShowEmojiPicker}
                    closeEmojiPicker={handleHideEmojiPicker}
                    disabled={disableEditMode}
                  />
                )}

                {hasPermission(Permissions.SEQUENCE_STEP_UPDATE) &&
                  enableMergeTag && (
                    <TagsDropdown
                      mergeTags={mergeTagOptions}
                      onAddMergeTag={handleMergeTagSelection}
                      onAddVariableTag={handleAddVariableTag}
                      disabled={disableEditMode}
                      placement={Placement.BottomEnd}
                      hideVariableTags={['{{SenderSignature}}']}
                    >
                      <div
                        style={{
                          cursor: disableEditMode ? 'not-allowed' : 'default',
                        }}
                      >
                        {!disableEditMode ? (
                          <OverlayTooltip
                            text="Add Mergetag"
                            placement={Placement.Bottom}
                            className="sidenav__item-tooltip"
                          >
                            <div
                              className="spintax-icon h-18px"
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <Braces2 height={18} width={18} />
                            </div>
                          </OverlayTooltip>
                        ) : (
                          <div
                            className="spintax-icon h-18px"
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                              cursor: 'not-allowed',
                            }}
                          >
                            <Braces2 height={18} width={18} />
                          </div>
                        )}
                      </div>
                    </TagsDropdown>
                  )}

                {hasPermission(Permissions.SEQUENCE_STEP_UPDATE) &&
                  enableSpintax && (
                    <div
                      style={{
                        cursor: disableEditMode ? 'not-allowed' : 'default',
                      }}
                    >
                      <SpintaxDropdown
                        show={showSpintax}
                        handleOnSpintaxToggle={handleOnSpintaxToggle}
                        handleAddSpintax={handleAddSpintax}
                        spintaxFor="subject"
                        disabled={disableEditMode}
                      />
                    </div>
                  )}
              </div>
              {/* </div> */}
            </div>
          </div>
        )}

        {inputErrorMessage && (
          <div className="d-flex justify-content-between align-items-center email-modal-input-error-wrapper">
            <div className="email-modal-input-error">
              <img src={Images.AlertTriangleRed} alt="Alert" />
              {inputErrorMessage}
            </div>
            {showInputCount && (
              <p
                className="subject-length"
                style={{
                  opacity: disableEditMode ? 0.6 : 1,
                  color: disableEditMode ? '#999' : undefined,
                }}
              >
                {showInputCount}
              </p>
            )}
          </div>
        )}

        {!inputErrorMessage && inputValue?.length > 0 && (
          <div className={helperWrapperClassName}>
            {inputRecommendation && (
              <>
                <p className="recommendation">
                  {t('labels.pre_header_recommendation')}
                </p>
              </>
            )}
            {showInputCount && (
              <p
                className="subject-length"
                style={{
                  opacity: disableEditMode ? 0.6 : 1,
                  color: disableEditMode ? '#999' : undefined,
                }}
              >
                {showInputCount}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SingleEditorInput;
